import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

const TokenValidationContext = createContext();
const API_BASE = process.env.REACT_APP_API_URL;
const PUBLIC_PATHS = [
    '/invoice/:guid',
    '/job/:guid',
    '/payment-success',
    '/payment-cancel',
    '/payments/:guid',
    '/login',
];

const isPublicPath = (pathname) => (
    PUBLIC_PATHS.some((path) => matchPath({ path, end: true }, pathname))
);

export const TokenValidationProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    const redirectToLoginIfProtected = useCallback(() => {
        if (!isPublicPath(pathnameRef.current)) {
            navigate('/login');
        }
    }, [navigate]);

    const refreshToken = useCallback(async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            redirectToLoginIfProtected();
            return;
        }

        try {
            const url = API_BASE + '/api/token/refresh/';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken })
            });

            if (!response.ok) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                redirectToLoginIfProtected();
            } else {
                const data = await response.json();
                localStorage.setItem('access_token', data.access);
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            redirectToLoginIfProtected();
        }
    }, [redirectToLoginIfProtected]);

    useEffect(() => {
        // Refresh token on page load
        refreshToken();

        // Set an interval to refresh token every hour
        const intervalId = setInterval(refreshToken, 3600000);

        // Clear the interval on component unmount
        return () => clearInterval(intervalId);
    }, [refreshToken]);

    return (
        <TokenValidationContext.Provider value={{ refreshToken }}>
            {children}
        </TokenValidationContext.Provider>
    );
};

export const useTokenValidation = () => {
    return useContext(TokenValidationContext);
};
